/**
 * Tab de proyecciones what-if. Permite armar una lista de gastos/ingresos
 * imaginarios (con recurrencia) y ver el impacto sobre los próximos N meses
 * vs el baseline real. Muestra series con Recharts.
 */
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Plus, Trash2, TrendingUp, TrendingDown, Calculator } from "lucide-react";
import clsx from "clsx";
import { RECURRENCES, type Recurrence } from "@axorial/shared";
import { api } from "@/lib/api";
import { formatMxnCents, formatPeriod } from "@/lib/format";

interface Hypothetical {
  uiKey: string; // local id, no se manda al server
  label: string;
  kind: "ingreso" | "gasto";
  amountPesos: number; // input en pesos
  ivaPesos: number;
  recurrence: Recurrence;
  facturado: boolean;
  firstMonth?: string;
}

interface CashFlow {
  ingresosCobradosCents: number;
  gastosPagadosCents: number;
  isrCents: number;
  ivaSaldoCents: number;
  netCashCents: number;
}

interface ProjectionResponse {
  startMonth: string;
  monthsAhead: number;
  months: Array<{
    period: string;
    baseline: CashFlow;
    projected: CashFlow;
    hypoCount: number;
  }>;
  totals: { baseline: CashFlow; projected: CashFlow };
}

const RECURRENCE_LABEL: Record<Recurrence, string> = {
  none: "Una vez",
  weekly: "Semanal",
  monthly: "Mensual",
  quarterly: "Trimestral",
  yearly: "Anual",
};

export function ProjectionsTab({ period }: { period: string }) {
  const [monthsAhead, setMonthsAhead] = useState(6);
  const [hypos, setHypos] = useState<Hypothetical[]>([]);

  const project = useMutation<ProjectionResponse>({
    mutationFn: () =>
      api.post("/finance/projections/run", {
        startMonth: period,
        monthsAhead,
        hypotheticals: hypos.map((h) => ({
          label: h.label,
          kind: h.kind,
          subtotalCents: Math.round(h.amountPesos * 100),
          ivaCents: Math.round(h.ivaPesos * 100),
          recurrence: h.recurrence,
          facturado: h.facturado,
          firstMonth: h.firstMonth,
        })),
      }),
  });

  const addHypo = () =>
    setHypos((prev) => [
      ...prev,
      {
        uiKey: crypto.randomUUID(),
        label: "",
        kind: "gasto",
        amountPesos: 0,
        ivaPesos: 0,
        recurrence: "monthly",
        facturado: true,
      },
    ]);

  const updateHypo = (uiKey: string, patch: Partial<Hypothetical>) =>
    setHypos((prev) => prev.map((h) => (h.uiKey === uiKey ? { ...h, ...patch } : h)));

  const removeHypo = (uiKey: string) => setHypos((prev) => prev.filter((h) => h.uiKey !== uiKey));

  return (
    <div className="space-y-6">
      <div className="card-elevated p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
              Escenarios hipotéticos
            </h3>
            <p className="text-xs text-base-content/55 mt-1 leading-relaxed">
              Agrega gastos o ingresos imaginarios para proyectar viabilidad. Marca recurrencia si
              se repite. La proyección NO toca tus datos reales.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-base-content/65 inline-flex items-center gap-2">
              Meses a proyectar
              <select
                value={monthsAhead}
                onChange={(e) => setMonthsAhead(Number(e.target.value))}
                className="select select-bordered select-xs"
              >
                {[3, 6, 12, 18, 24].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={addHypo} className="btn btn-ghost btn-sm">
              <Plus className="size-4" /> Agregar
            </button>
          </div>
        </div>

        {hypos.length === 0 ? (
          <div className="text-center py-8 text-sm text-base-content/55 italic">
            Sin hipotéticos. Click <strong>Agregar</strong> para empezar a probar escenarios.
          </div>
        ) : (
          <ul className="space-y-2">
            {hypos.map((h) => (
              <HypoRow
                key={h.uiKey}
                hypo={h}
                period={period}
                onChange={(patch) => updateHypo(h.uiKey, patch)}
                onRemove={() => removeHypo(h.uiKey)}
              />
            ))}
          </ul>
        )}

        <div className="pt-2 border-t border-base-300/60 flex items-center justify-end">
          <button
            type="button"
            onClick={() => project.mutate()}
            disabled={project.isPending}
            className="btn btn-primary btn-sm"
          >
            {project.isPending && <span className="loading loading-spinner loading-xs" />}
            <Calculator className="size-4" /> Proyectar
          </button>
        </div>
      </div>

      {project.data && <ProjectionResult data={project.data} hypoCount={hypos.length} />}
    </div>
  );
}

function HypoRow({
  hypo,
  period,
  onChange,
  onRemove,
}: {
  hypo: Hypothetical;
  period: string;
  onChange: (patch: Partial<Hypothetical>) => void;
  onRemove: () => void;
}) {
  return (
    <li className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start p-3 rounded-md border border-base-300/60 bg-base-200/30">
      <input
        className="input input-bordered input-sm md:col-span-3"
        placeholder="Concepto (ej. Hosting AWS)"
        value={hypo.label}
        onChange={(e) => onChange({ label: e.target.value })}
      />
      <select
        value={hypo.kind}
        onChange={(e) => onChange({ kind: e.target.value as Hypothetical["kind"] })}
        className="select select-bordered select-sm md:col-span-2"
      >
        <option value="gasto">Gasto</option>
        <option value="ingreso">Ingreso</option>
      </select>
      <input
        type="number"
        step="0.01"
        className="input input-bordered input-sm md:col-span-2"
        placeholder="Subtotal"
        value={hypo.amountPesos || ""}
        onChange={(e) => onChange({ amountPesos: Number(e.target.value) })}
      />
      <input
        type="number"
        step="0.01"
        className="input input-bordered input-sm md:col-span-1"
        placeholder="IVA"
        value={hypo.ivaPesos || ""}
        onChange={(e) => onChange({ ivaPesos: Number(e.target.value) })}
        title="IVA del hipotético — usa 16% del subtotal si aplica"
      />
      <select
        value={hypo.recurrence}
        onChange={(e) => onChange({ recurrence: e.target.value as Recurrence })}
        className="select select-bordered select-sm md:col-span-2"
      >
        {RECURRENCES.map((r) => (
          <option key={r} value={r}>
            {RECURRENCE_LABEL[r]}
          </option>
        ))}
      </select>
      <input
        type="month"
        className="input input-bordered input-sm md:col-span-1"
        value={hypo.firstMonth ?? period}
        onChange={(e) => onChange({ firstMonth: e.target.value || undefined })}
        title="Mes en que arranca"
      />
      <button
        type="button"
        onClick={onRemove}
        className="btn btn-ghost btn-sm btn-square text-error md:col-span-1 justify-self-end"
        title="Quitar"
      >
        <Trash2 className="size-3.5" />
      </button>
      {hypo.kind === "gasto" && (
        <label className="md:col-span-12 cursor-pointer label justify-start gap-2 py-0 text-xs">
          <input
            type="checkbox"
            className="checkbox checkbox-xs checkbox-primary"
            checked={hypo.facturado}
            onChange={(e) => onChange({ facturado: e.target.checked })}
          />
          Facturado (cuenta para IVA acreditable)
        </label>
      )}
    </li>
  );
}

function ProjectionResult({ data, hypoCount }: { data: ProjectionResponse; hypoCount: number }) {
  // Convertimos cents a pesos para que las gráficas lean números legibles.
  const chartData = useMemo(
    () =>
      data.months.map((m) => ({
        period: m.period.slice(2), // "26-05"
        baselineNet: m.baseline.netCashCents / 100,
        projectedNet: m.projected.netCashCents / 100,
        ingresos: m.projected.ingresosCobradosCents / 100,
        gastos: -m.projected.gastosPagadosCents / 100,
        isr: -m.projected.isrCents / 100,
      })),
    [data.months],
  );

  const deltaNet = data.totals.projected.netCashCents - data.totals.baseline.netCashCents;
  const deltaIsr = data.totals.projected.isrCents - data.totals.baseline.isrCents;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DeltaCard
          label="Δ Caja neta"
          value={deltaNet}
          tone={deltaNet >= 0 ? "success" : "error"}
          hint={`${formatMxnCents(data.totals.baseline.netCashCents)} → ${formatMxnCents(data.totals.projected.netCashCents)}`}
        />
        <DeltaCard
          label="Δ ISR a pagar"
          value={deltaIsr}
          tone={deltaIsr <= 0 ? "success" : "warning"}
          hint={`${data.monthsAhead} meses, ${hypoCount} ${hypoCount === 1 ? "hipotético" : "hipotéticos"}`}
        />
        <div className="card-elevated p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-2">
            Periodo
          </div>
          <div className="text-sm">
            {formatPeriod(data.startMonth)} →{" "}
            {formatPeriod(data.months[data.months.length - 1]!.period)}
          </div>
          <div className="text-xs text-base-content/55 mt-1">
            {data.monthsAhead} meses incluyendo el actual
          </div>
        </div>
      </div>

      <div className="card-elevated p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 mb-3">
          Caja neta proyectada vs baseline
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
              <XAxis dataKey="period" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) =>
                  typeof v === "number"
                    ? v.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
                    : String(v)
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="ingresos" name="Ingresos proyectados" fill="oklch(0.78 0.16 145)" />
              <Bar dataKey="gastos" name="Gastos proyectados" fill="oklch(0.65 0.2 25)" />
              <Bar dataKey="isr" name="ISR" fill="oklch(0.78 0.18 75)" />
              <Line
                dataKey="baselineNet"
                name="Caja baseline"
                stroke="oklch(0.55 0.05 280)"
                strokeDasharray="4 3"
                dot={false}
                strokeWidth={2}
              />
              <Line
                dataKey="projectedNet"
                name="Caja proyectada"
                stroke="oklch(0.62 0.18 220)"
                dot={{ r: 3 }}
                strokeWidth={2}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-elevated p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 mb-3">
          Detalle por mes
        </h3>
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Mes</th>
                <th className="text-right">Ingresos</th>
                <th className="text-right">Gastos</th>
                <th className="text-right">ISR</th>
                <th className="text-right">Caja neta</th>
                <th className="text-right">Δ vs baseline</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m) => {
                const delta = m.projected.netCashCents - m.baseline.netCashCents;
                return (
                  <tr key={m.period}>
                    <td className="capitalize">{formatPeriod(m.period)}</td>
                    <td className="text-right font-mono tabular-nums text-success">
                      {formatMxnCents(m.projected.ingresosCobradosCents)}
                    </td>
                    <td className="text-right font-mono tabular-nums text-error">
                      −{formatMxnCents(m.projected.gastosPagadosCents)}
                    </td>
                    <td className="text-right font-mono tabular-nums">
                      −{formatMxnCents(m.projected.isrCents)}
                    </td>
                    <td
                      className={clsx(
                        "text-right font-mono tabular-nums font-semibold",
                        m.projected.netCashCents >= 0 ? "text-success" : "text-error",
                      )}
                    >
                      {formatMxnCents(m.projected.netCashCents)}
                    </td>
                    <td
                      className={clsx(
                        "text-right font-mono tabular-nums",
                        delta > 0
                          ? "text-success"
                          : delta < 0
                            ? "text-error"
                            : "text-base-content/55",
                      )}
                    >
                      {delta > 0 ? "+" : ""}
                      {formatMxnCents(delta)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DeltaCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: "success" | "error" | "warning";
  hint?: string;
}) {
  const colors = {
    success: "text-success",
    error: "text-error",
    warning: "text-warning",
  };
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="card-elevated p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
          {label}
        </div>
        <Icon className={clsx("size-4", colors[tone])} />
      </div>
      <div className={clsx("mt-2 text-2xl font-display font-bold tabular-nums", colors[tone])}>
        {value >= 0 ? "+" : ""}
        {formatMxnCents(value)}
      </div>
      {hint && <div className="text-xs text-base-content/55 mt-1">{hint}</div>}
    </div>
  );
}
